import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminMembrane,
  InsertMembraneBody,
  UpdateMembraneNewImageBody,
  UpdateMembraneRemoveImageBody,
} from "./types/membranes";

export const useMembranesApi = () => {
  const api = useApi();

  const getAdminMembrane = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/membranes`;
      return useSWRMutation<AdminMembrane, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminMembrane>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminMembranes = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/patrol/membranes", query);
      return useSWR<Paginated<AdminMembrane>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminMembrane>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminMembrane = useCallback(
    async (body: InsertMembraneBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/membranes`;
      const id = await api.post<{ id: string }, InsertMembraneBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminMembrane = useCallback(
    async (membraneId: string, body: UpdateMembraneRemoveImageBody | UpdateMembraneNewImageBody): Promise<void> => {
      const url = `/admin/settings/patrol/membranes/${membraneId}`;
      await api.put<UpdateMembraneRemoveImageBody | UpdateMembraneNewImageBody>(url, body);
      mutateCache(["/admin/settings/patrol/membranes"]);
    },
    [api],
  );

  const deleteAdminMembrane = useCallback(
    async (membraneId: string): Promise<void> => {
      const url = `/admin/settings/patrol/membranes/${membraneId}`;
      await api.del(url);
      mutateCache([`/admin/settings/patrol/membranes`]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (membraneId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/patrol/membranes/${membraneId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  const updateAdminMembraneVisibility = useCallback(
    async (membraneId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/membranes/${membraneId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/patrol/membranes"]);
    },
    [api],
  );

  const updateAdminMembraneOrder = useCallback(
    async (membraneId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/membranes/${membraneId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/settings/patrol/membranes"]);
    },
    [api],
  );

  return {
    getAdminMembrane,
    useAdminMembranes,
    insertAdminMembrane,
    updateAdminMembrane,
    updateAdminMembraneVisibility,
    updateAdminMembraneOrder,
    deleteAdminMembrane,
    generateUploadImageUrl,
  };
};
